import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  getPets, adminCreatePet, adminFeaturePet, adminDeletePet,
  getEditions, adminCreateEdition, adminDeleteEdition,
  adminClassifieds, adminDeleteClassified,
  adminDeals, adminCreateDeal, adminDeleteDeal,
  adminContests, adminCreateContest, adminContestEntries, adminDeleteContest,
} from "../lib/api";
import { Toolbar, Modal, Field, Check, Card, inputCls } from "./AdminLayout";
import { IconBtn } from "./ArticlesAdmin";
import ImageUpload from "../components/ImageUpload";
import { Trash2, Star, PawPrint, BookOpen, Tag, Flame, Trophy, Users } from "lucide-react";

const TABS = [
  { id: "pets", label: "Adoptable Pets", icon: PawPrint },
  { id: "editions", label: "e-Editions", icon: BookOpen },
  { id: "classifieds", label: "Classifieds", icon: Tag },
  { id: "deals", label: "Daily Deal", icon: Flame },
];

export default function CommunityAdmin() {
  const [tab, setTab] = useState("pets");
  return (
    <div>
      <div className="mb-5 flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`community-tab-${t.id}`}
            className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold ${tab === t.id ? "bg-[var(--navy)] text-white" : "bg-white text-slate-600 border border-slate-200"}`}>
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>
      {tab === "pets" && <PetsTab />}
      {tab === "editions" && <EditionsTab />}
      {tab === "classifieds" && <ClassifiedsTab />}
      {tab === "deals" && <DealsTab />}
      {tab === "contests" && <ContestsTab />}
    </div>
  );
}

function PetsTab() {
  const empty = { name: "", species: "Dog", breed: "", age: "", sex: "", rescue: "", description: "", image: "", featured: false, status: "Available" };
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const load = () => getPets("All").then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Pet name required.");
    setBusy(true);
    try { await adminCreatePet(form); toast.success("Pet listed."); setOpen(false); setForm(empty); load(); }
    catch { toast.error("Could not save."); } finally { setBusy(false); }
  };
  const feature = async (id) => { await adminFeaturePet(id); toast.success("Set as Pet of the Week."); load(); };
  const remove = async (p) => { if (!window.confirm(`Delete ${p.name}?`)) return; await adminDeletePet(p.id); load(); };
  return (
    <div>
      <Toolbar title="Adoptable Pets" subtitle="Rescue partners list dogs & cats; mark one as Pet of the Week" onAdd={() => { setForm(empty); setOpen(true); }} addLabel="Add Pet" />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((p) => (
          <Card key={p.id} data-testid={`admin-pet-${p.id}`} className="overflow-hidden">
            <div className="relative h-40"><img src={p.image} alt={p.name} className="h-full w-full object-cover" />
              {p.featured && <span className="absolute left-2 top-2 rounded-full bg-[var(--gold)] px-2 py-0.5 text-[10px] font-semibold text-white">Pet of the Week</span>}</div>
            <div className="p-3">
              <p className="font-bold text-[var(--navy)]">{p.name} <span className="text-xs font-normal text-slate-500">· {p.species} · {p.breed}</span></p>
              <p className="text-xs text-slate-500">{p.rescue}</p>
              <div className="mt-2 flex gap-2">
                {!p.featured && <button onClick={() => feature(p.id)} data-testid={`feature-pet-${p.id}`} className="flex items-center gap-1 rounded-lg bg-[var(--gold)]/15 px-2 py-1 text-xs font-semibold text-[var(--gold)]"><Star size={12} /> Feature</button>}
                <IconBtn onClick={() => remove(p)} danger testid={`delete-pet-${p.id}`}><Trash2 size={14} /></IconBtn>
              </div>
            </div>
          </Card>
        ))}
      </div>
      {open && (
        <Modal title="Add Adoptable Pet" onClose={() => setOpen(false)} onSubmit={submit} busy={busy}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Name"><input data-testid="pet-name" value={form.name} onChange={set("name")} className={inputCls} /></Field>
            <Field label="Species"><select data-testid="pet-species" value={form.species} onChange={set("species")} className={inputCls}><option>Dog</option><option>Cat</option><option>Other</option></select></Field>
            <Field label="Breed"><input value={form.breed} onChange={set("breed")} className={inputCls} /></Field>
            <Field label="Age"><input value={form.age} onChange={set("age")} className={inputCls} /></Field>
            <Field label="Sex"><input value={form.sex} onChange={set("sex")} className={inputCls} /></Field>
            <Field label="Rescue group"><input data-testid="pet-rescue" value={form.rescue} onChange={set("rescue")} className={inputCls} /></Field>
            <Field label="Description" full><textarea value={form.description} onChange={set("description")} rows={2} className={inputCls} /></Field>
            <Field label="Photo" full><ImageUpload value={form.image} onChange={(url) => setForm((f) => ({ ...f, image: url }))} testid="pet-image" /></Field>
            <div className="sm:col-span-2"><Check label="Make Pet of the Week" checked={form.featured} onChange={() => setForm({ ...form, featured: !form.featured })} testid="pet-featured" /></div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function EditionsTab() {
  const empty = { title: "", issue_date: "", pagesText: "" };
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const load = () => getEditions().then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const addPage = (url) => setForm((f) => ({ ...f, pagesText: (f.pagesText ? f.pagesText + "\n" : "") + url }));
  const submit = async (e) => {
    e.preventDefault();
    const pages = form.pagesText.split("\n").map((l) => l.trim()).filter(Boolean);
    if (!form.title || !pages.length) return toast.error("Title and at least one page required.");
    setBusy(true);
    try { await adminCreateEdition({ title: form.title, issue_date: form.issue_date, pages, cover: pages[0] }); toast.success("Edition published."); setOpen(false); setForm(empty); load(); }
    catch { toast.error("Could not save."); } finally { setBusy(false); }
  };
  const remove = async (ed) => { if (!window.confirm("Delete this edition?")) return; await adminDeleteEdition(ed.id); load(); };
  return (
    <div>
      <Toolbar title="e-Editions" subtitle="Upload page images to publish a flip-book replica" onAdd={() => { setForm(empty); setOpen(true); }} addLabel="New Edition" />
      <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-5">
        {items.map((ed) => (
          <Card key={ed.id} data-testid={`admin-edition-${ed.id}`} className="overflow-hidden">
            <img src={ed.cover} alt={ed.title} className="h-48 w-full object-cover object-top" />
            <div className="flex items-center justify-between p-3">
              <div><p className="text-sm font-bold text-[var(--navy)]">{ed.issue_date}</p><p className="text-xs text-slate-500">{ed.pages.length} pages</p></div>
              <IconBtn onClick={() => remove(ed)} danger testid={`delete-edition-${ed.id}`}><Trash2 size={14} /></IconBtn>
            </div>
          </Card>
        ))}
      </div>
      {open && (
        <Modal title="New e-Edition" onClose={() => setOpen(false)} onSubmit={submit} busy={busy} submitLabel="Publish">
          <Field label="Title"><input data-testid="ed-title" value={form.title} onChange={set("title")} className={inputCls} placeholder="News-Leader — Oct 15, 2026" /></Field>
          <Field label="Issue date"><input data-testid="ed-date" type="date" value={form.issue_date} onChange={set("issue_date")} className={inputCls} /></Field>
          <Field label="Add page images (upload each, in order)"><ImageUpload value="" onChange={addPage} testid="ed-page-upload" label="Page" /></Field>
          <Field label="Page URLs (one per line)"><textarea data-testid="ed-pages" value={form.pagesText} onChange={set("pagesText")} rows={4} className={inputCls} /></Field>
        </Modal>
      )}
    </div>
  );
}

function ClassifiedsTab() {
  const [items, setItems] = useState([]);
  const load = () => adminClassifieds().then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);
  const remove = async (c) => { if (!window.confirm("Remove this ad?")) return; await adminDeleteClassified(c.id); load(); };
  return (
    <div>
      <Toolbar title="Classifieds" subtitle="Self-serve ads submitted by advertisers — moderate here" />
      <Card className="overflow-hidden">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr><th className="px-4 py-3">Ad</th><th className="px-4 py-3">Category</th><th className="px-4 py-3">Contact</th><th className="px-4 py-3 text-right">Action</th></tr></thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((c) => (
              <tr key={c.id} data-testid={`admin-classified-${c.id}`}>
                <td className="px-4 py-3 font-semibold text-[var(--navy)]">{c.title} {c.price && <span className="text-slate-500">· {c.price}</span>}</td>
                <td className="px-4 py-3 text-slate-600">{c.category}</td>
                <td className="px-4 py-3 text-slate-500">{c.name} · {c.contact}</td>
                <td className="px-4 py-3 text-right"><IconBtn onClick={() => remove(c)} danger testid={`delete-classified-${c.id}`}><Trash2 size={14} /></IconBtn></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function DealsTab() {
  const empty = { business: "", title: "", description: "", discount: "", image: "" };
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const load = () => adminDeals().then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.business) return toast.error("Business and title required.");
    setBusy(true);
    try { await adminCreateDeal(form); toast.success("Deal is now live (newest shows on site)."); setOpen(false); setForm(empty); load(); }
    catch { toast.error("Could not save."); } finally { setBusy(false); }
  };
  const remove = async (d) => { if (!window.confirm("Delete deal?")) return; await adminDeleteDeal(d.id); load(); };
  return (
    <div>
      <Toolbar title="Daily Deal" subtitle="The newest deal is featured on the homepage" onAdd={() => { setForm(empty); setOpen(true); }} addLabel="New Deal" />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((d) => (
          <Card key={d.id} data-testid={`admin-deal-${d.id}`} className="overflow-hidden">
            {d.image && <img src={d.image} alt={d.title} className="h-32 w-full object-cover" />}
            <div className="p-3">
              <p className="text-xs font-semibold uppercase text-[var(--gold)]">{d.business}</p>
              <p className="font-bold text-[var(--navy)]">{d.title} {d.discount && <span className="text-[var(--breaking)]">{d.discount}</span>}</p>
              <IconBtn onClick={() => remove(d)} danger testid={`delete-deal-${d.id}`}><Trash2 size={14} /></IconBtn>
            </div>
          </Card>
        ))}
      </div>
      {open && (
        <Modal title="New Daily Deal" onClose={() => setOpen(false)} onSubmit={submit} busy={busy}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Business"><input data-testid="deal-business" value={form.business} onChange={set("business")} className={inputCls} /></Field>
            <Field label="Discount/Price"><input value={form.discount} onChange={set("discount")} className={inputCls} placeholder="$24.99" /></Field>
            <Field label="Title" full><input data-testid="deal-title" value={form.title} onChange={set("title")} className={inputCls} /></Field>
            <Field label="Description" full><textarea value={form.description} onChange={set("description")} rows={2} className={inputCls} /></Field>
            <Field label="Photo" full><ImageUpload value={form.image} onChange={(url) => setForm((f) => ({ ...f, image: url }))} testid="deal-image" /></Field>
          </div>
        </Modal>
      )}
    </div>
  );
}

function ContestsTab() {
  const empty = { title: "", prize: "", description: "", image: "", end_date: "", active: true };
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [entries, setEntries] = useState(null);
  const load = () => adminContests().then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.title) return toast.error("Title required.");
    setBusy(true);
    try { await adminCreateContest(form); toast.success("Contest launched."); setOpen(false); setForm(empty); load(); }
    catch { toast.error("Could not save."); } finally { setBusy(false); }
  };
  const remove = async (c) => { if (!window.confirm("Delete contest?")) return; await adminDeleteContest(c.id); load(); };
  const viewEntries = async (c) => { const e = await adminContestEntries(c.id); setEntries({ contest: c, list: e }); };
  return (
    <div>
      <Toolbar title="Contests" subtitle="Run giveaways and capture entrant emails" onAdd={() => { setForm(empty); setOpen(true); }} addLabel="New Contest" />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((c) => (
          <Card key={c.id} data-testid={`admin-contest-${c.id}`} className="overflow-hidden">
            {c.image && <img src={c.image} alt={c.title} className="h-32 w-full object-cover" />}
            <div className="p-3">
              <p className="font-bold text-[var(--navy)]">{c.title}</p>
              <p className="text-xs text-slate-500">{c.prize}</p>
              <div className="mt-2 flex gap-2">
                <button onClick={() => viewEntries(c)} data-testid={`entries-${c.id}`} className="flex items-center gap-1 rounded-lg bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-600"><Users size={12} /> Entries</button>
                <IconBtn onClick={() => remove(c)} danger testid={`delete-contest-${c.id}`}><Trash2 size={14} /></IconBtn>
              </div>
            </div>
          </Card>
        ))}
      </div>
      {open && (
        <Modal title="New Contest" onClose={() => setOpen(false)} onSubmit={submit} busy={busy} submitLabel="Launch">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Title" full><input data-testid="contest-title" value={form.title} onChange={set("title")} className={inputCls} /></Field>
            <Field label="Prize"><input data-testid="contest-prize" value={form.prize} onChange={set("prize")} className={inputCls} /></Field>
            <Field label="End date"><input type="date" value={form.end_date} onChange={set("end_date")} className={inputCls} /></Field>
            <Field label="Description" full><textarea value={form.description} onChange={set("description")} rows={2} className={inputCls} /></Field>
            <Field label="Photo" full><ImageUpload value={form.image} onChange={(url) => setForm((f) => ({ ...f, image: url }))} testid="contest-image" /></Field>
          </div>
        </Modal>
      )}
      {entries && (
        <Modal title={`Entries — ${entries.contest.title}`} onClose={() => setEntries(null)} onSubmit={(e) => { e.preventDefault(); setEntries(null); }} submitLabel="Close">
          <p className="mb-2 text-sm font-semibold text-slate-600">{entries.list.length} entries collected</p>
          <div className="max-h-80 overflow-y-auto rounded-lg border border-slate-200">
            {entries.list.map((en, i) => (
              <div key={i} className="flex justify-between border-b border-slate-100 px-3 py-2 text-sm"><span>{en.name}</span><span className="text-slate-500">{en.email}</span></div>
            ))}
            {entries.list.length === 0 && <p className="p-4 text-center text-sm text-slate-400">No entries yet.</p>}
          </div>
        </Modal>
      )}
    </div>
  );
}
